import type { InsertMediaData, InsertMediaPrecursor } from './insertMediaData'
import Plugin from '@ckeditor/ckeditor5-core/src/plugin'
import { Command } from '@ckeditor/ckeditor5-core'
import { ButtonView, View } from '@ckeditor/ckeditor5-ui'
import videoIcon from '@ckeditor/ckeditor5-media-embed/theme/icons/media.svg'
import { t, tp } from '@ulule/localize'
import { Locale, first } from '@ckeditor/ckeditor5-utils'
import type { EditorConfig } from '@ckeditor/ckeditor5-core/src/editor/editorconfig'
import { DowncastWriter } from '@ckeditor/ckeditor5-engine'
import ContainerElement from '@ckeditor/ckeditor5-engine/src/view/containerelement'
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils'
import ViewElement from '@ckeditor/ckeditor5-engine/src/view/element'

import '@ckeditor/ckeditor5-media-embed/theme/mediaembedediting.css'
import '@ckeditor/ckeditor5-media-embed/theme/mediaembed.css'
import './theme/insert-media.css'

export class UluleMediaInsert extends Plugin {
  static get requires() {
    return [UluleInsertMediaEditing, UluleMediaInsertUI]
  }
}

class UluleMediaInsertUI extends Plugin {
  init() {
    const editor = this.editor

    const componentCreator = this._getComponentCreator()

    if (componentCreator !== undefined) {
      editor.ui.componentFactory.add('ululeInsertMedia', componentCreator)
      editor.ui.componentFactory.add('ululeMediaInsert', componentCreator)
    }
  }

  private _getComponentCreator(): ((locale: Locale) => View) | undefined {
    const pluginConfig = this._getPluginConfig()

    if (pluginConfig === undefined) {
      return
    }

    const editor = this.editor

    const command = editor.commands.get('ululeInsertMedia') as UluleInsertMediaCommand
    if (command === undefined) {
      throw Error('no command insertVideo found')
    }

    return (locale) => {
      const buttonView = new ButtonView(locale)

      buttonView.set({
        label: t('Add media'),
        icon: videoIcon,
        tooltip: true,
      })

      buttonView.bind('isOn', 'isEnabled').to(command, 'value', 'isEnabled')

      this.listenTo(buttonView, 'execute', () => {
        const data = this._getSelectedMediaData()

        pluginConfig.setCommands({
          insertMedia: (data: InsertMediaData) => command.execute(data),
          initialData: data,
        })
      })

      return buttonView
    }
  }

  private _getSelectedMediaData(): Partial<InsertMediaPrecursor> {
    const selectedBlocks = Array.from(this.editor.model.document.selection.getSelectedBlocks())

    if (selectedBlocks.length === 1) {
      const element = selectedBlocks[0]

      if (element.name === 'ululeMedia') {
        const ratio = element.getAttribute('ratio')
        return { url: String(element.getAttribute('url')), ratio: ratio === undefined ? undefined : String(ratio) }
      }
    }

    return {}
  }

  private _getSafeGlobalConfig(): EditorConfig {
    // HACK: we can't use `this.editor.config.get(...) because
    // internally data is cloned by lodash, and this don't work well with
    // functions. We are forced to use the private/secret `config._config`
    // property to access the function without cloning.
    //@ts-expect-error: hack
    return this.editor.config._config
  }

  private _getPluginConfig(): EditorConfig['insertMedia'] {
    return this._getSafeGlobalConfig().insertMedia
  }
}

class UluleInsertMediaEditing extends Plugin {
  init() {
    const editor = this.editor
    const schema = editor.model.schema
    const conversion = editor.conversion

    editor.commands.add('ululeInsertMedia', new UluleInsertMediaCommand(this.editor))

    // Configure the schema.
    schema.register('ululeMedia', {
      inheritAllFrom: '$blockObject',
      allowAttributes: ['url', 'html', 'ratio'],
    })

    // Model -> Data
    conversion.for('dataDowncast').elementToElement({
      model: 'ululeMedia',
      view: (modelElement, { writer }) => {
        const url = String(modelElement.getAttribute('url'))
        const ratio = String(modelElement.getAttribute('ratio'))
        const html = String(modelElement.getAttribute('html'))

        return createMediaFigureElement(
          writer,
          {
            url,
            html,
            ratio,
          },
          false,
        )
      },
    })

    // Model -> View (element)
    conversion.for('editingDowncast').elementToElement({
      model: 'ululeMedia',
      view: (modelElement, { writer }) => {
        const url = String(modelElement.getAttribute('url'))
        const html = String(modelElement.getAttribute('html'))
        const ratio = String(modelElement.getAttribute('ratio'))

        const figure = createMediaFigureElement(
          writer,
          {
            url,
            html,
            ratio,
          },
          true,
        )

        return toMediaWidget(
          figure,
          writer,
          tp(
            'Media embed widget',
            'Text that describes the rich editor (CKEditor) media embed widget for screen readers.',
          ),
        )
      },
    })

    // View -> Model (data-oembed-url -> url)
    conversion
      .for('upcast')
      .elementToElement({
        view: {
          name: 'div',
          attributes: {
            'data-ulule-oembed-url': true,
            'data-ulule-oembed-ratio': true,
          },
        },
        model: (viewMedia, { writer }) => {
          const url = String(viewMedia.getAttribute('data-ulule-oembed-url'))
          const ratio = String(viewMedia.getAttribute('data-ulule-oembed-ratio'))

          const container = document.createElement('div')
          container.append(...Array.from(editor.editing.view.domConverter.viewChildrenToDom(viewMedia)))

          return writer.createElement('ululeMedia', { url, html: container.innerHTML, ratio })
        },
      })
      .elementToElement({
        view: {
          name: 'iframe',
        },
        model: (iframeView, { writer }) => {
          const url = String(iframeView.getAttribute('src'))

          const container = document.createElement('div')
          container.append(editor.editing.view.domConverter.viewToDom(iframeView))

          return writer.createElement('ululeMedia', { url, html: container.innerHTML })
        },
      })
      // Consume `<figure class="media">` elements, that were left after upcast.
      .add((dispatcher) => {
        dispatcher.on('element:figure', (evt, data, conversionApi) => {
          if (!conversionApi.consumable.consume(data.viewItem, { name: true, classes: 'media' })) {
            return
          }

          const { modelRange, modelCursor } = conversionApi.convertChildren(data.viewItem, data.modelCursor)

          data.modelRange = modelRange
          data.modelCursor = modelCursor

          const modelElement = first(modelRange.getItems())

          if (!modelElement) {
            // Revert consumed figure so other features can convert it.
            conversionApi.consumable.revert(data.viewItem, { name: true, classes: 'media' })
          }
        })
      })
  }
}

function createMediaFigureElement(
  writer: DowncastWriter,
  props: {
    url: string
    html: string
    ratio: string
  },
  editing: boolean,
): ContainerElement {
  const attributes: Record<string, string> = {
    'data-ulule-oembed-url': props.url,
    'data-ulule-oembed-ratio': props.ratio,
    class: 'media-wrapper',
  }

  if (editing) {
    attributes.class = attributes.class + ' ck-media__wrapper'
  }

  const content = writer.createRawElement('div', attributes, (domElement, domConverter) => {
    domConverter.setContentOf(domElement, props.html)
  })

  const figure = writer.createContainerElement('figure', { class: 'media' })

  writer.insert(writer.createPositionAt(figure, 'end'), content)

  writer.setCustomProperty('media-content', true, content)

  return figure
}

function toMediaWidget(viewElement: ViewElement, writer: DowncastWriter, label: string): ViewElement {
  writer.setCustomProperty('media', true, viewElement)

  return toWidget(viewElement, writer, { label })
}

class UluleInsertMediaCommand extends Command {
  declare value: boolean
  declare isEnabled: boolean

  execute(option: InsertMediaData): void {
    this.editor.model.change((writer) => {
      const imageElement = writer.createElement('ululeMedia', option)
      this.editor.model.insertContent(imageElement)
    })
  }
}

export type InsertMediaCommands = {
  insertMedia: (data: InsertMediaData) => void
  initialData: Partial<InsertMediaPrecursor>
}

declare module '@ckeditor/ckeditor5-core/src/editor/editorconfig' {
  interface EditorConfig {
    insertMedia?: {
      setCommands: (commands: InsertMediaCommands) => void
    }
  }
}
